import { useState } from "react";
import { X } from "lucide-react";
import { createPortal } from "react-dom";
import { useAuth } from "../../context/AuthContext";

interface ProfileDrawerProps {
  open: boolean;
  activeTab: "profile" | "account";
  onClose: () => void;
  onTabChange: (tab: "profile" | "account") => void;
}

export default function ProfileDrawer({ open, activeTab, onClose, onTabChange }: ProfileDrawerProps) {
  const { user } = useAuth();
  const [theme, setTheme] = useState<"light" | "dark">("light");

  // ✅ Derive initials from real user name
  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  return createPortal(
    <>
      {/* BACKDROP */}
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-300 z-[999999]
          ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />

      {/* DRAWER */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-xl
          z-[1000000] transition-transform duration-300
          ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-5 border-b">
          <div className="flex gap-6">
            <button
              onClick={() => onTabChange("profile")}
              className={`pb-2 text-sm font-medium ${
                activeTab === "profile" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500"
              }`}
            >
              My Profile
            </button>
            <button
              onClick={() => onTabChange("account")}
              className={`pb-2 text-sm font-medium ${
                activeTab === "account" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500"
              }`}
            >
              Account Management
            </button>
          </div>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* CONTENT */}
        <div className="overflow-y-auto h-full px-6 py-6">

          {/* PROFILE TAB */}
          {activeTab === "profile" && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-100 p-5 rounded-xl flex gap-4">
                {/* ✅ Dynamic initials */}
                <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
                  <span className="text-white text-lg font-semibold">{initials}</span>
                </div>
                <div>
                  {/* ✅ Real user name */}
                  <p className="text-lg font-semibold text-gray-800">{user?.name ?? "—"}</p>
                  <p className="text-sm text-gray-500 capitalize">{user?.roleName ?? "—"}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-400">Email</p>
                  {/* ✅ Real user email */}
                  <p className="text-sm text-gray-700">{user?.email ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Role</p>
                  <p className="text-sm text-gray-700 capitalize">{user?.roleName ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Status</p>
                  <p className="text-sm text-green-600 font-medium">Active</p>
                </div>
              </div>
            </div>
          )}

          {/* ACCOUNT TAB */}
          {activeTab === "account" && (
            <div className="space-y-8">
              <div>
                <p className="uppercase text-xs tracking-wide text-gray-500 mb-3">Appearance</p>
                <div className="grid grid-cols-2 gap-4">
                  {/* ✅ Functional theme toggle */}
                  <button
                    onClick={() => setTheme("light")}
                    className={`border-2 p-4 rounded-xl text-left ${
                      theme === "light" ? "border-blue-600 bg-blue-50" : "border-gray-200"
                    }`}
                  >
                    <p className={`text-sm font-semibold ${theme === "light" ? "text-blue-600" : "text-gray-700"}`}>Light</p>
                    <p className={`text-xs ${theme === "light" ? "text-blue-600" : "text-gray-400"}`}>Clean & bright</p>
                  </button>
                  <button
                    onClick={() => setTheme("dark")}
                    className={`border-2 p-4 rounded-xl text-left ${
                      theme === "dark" ? "border-blue-600 bg-blue-50" : "border-gray-200"
                    }`}
                  >
                    <p className={`text-sm font-semibold ${theme === "dark" ? "text-blue-600" : "text-gray-700"}`}>Dark</p>
                    <p className={`text-xs ${theme === "dark" ? "text-blue-600" : "text-gray-400"}`}>Sleek & immersive</p>
                  </button>
                </div>
              </div>

              <div>
                <p className="uppercase text-xs tracking-wide text-gray-500 mb-3">Notifications</p>
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-sm text-gray-700">Push Notifications</span>
                  <input type="checkbox" className="toggle" defaultChecked />
                </div>
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-sm text-gray-700">Email Alerts</span>
                  <input type="checkbox" className="toggle" />
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-sm text-gray-700">Weekly Digest</span>
                  <input type="checkbox" className="toggle" defaultChecked />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
